<template>
  <div class="relative">
    <!-- Spacer div to prevent content jump -->
    <div 
      v-if="isScrolled || isMenuOpen"
      :style="{
        height: isScrolled || isMenuOpen ? '56px' : '72px',
        transition: 'height 300ms'
      }"
    />
    <!-- Navigation -->
    <nav 
      class="transition-all duration-300 w-full z-50" 
      :class="[
        isScrolled || isMenuOpen
          ? 'fixed top-0 bg-white dark:bg-black dark: shadow-lg py-2' 
          : 'bg-white/95 dark:bg-black/95 backdrop-blur-md py-4'
      ]"
      :style="{
        '--top-nav-height': isScrolled || isMenuOpen ? '56px' : '72px'
      }"
    >
      <div class="max-w-7xl mx-auto px-4">
        <div class="flex items-center">
          <!-- Brand -->
          <div 
            @click="loadHome" 
            class="flex items-center space-x-2 md:space-x-4 shrink-0 cursor-pointer group"
          >
            <img
              src="/logo.svg" 
              alt="Company Logo" 
              class="h-8 md:h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105 dark:invert"
            />
            <span class="text-lg md:text-xl font-bold text-slate-900 dark:text-white tracking-tight">
              {{ SITE_NAME }}
            </span>
          </div>

          <!-- Desktop Navigation -->
          <NavLinks class="hidden md:flex flex-1 min-w-0 px-4" />

          <!-- Actions -->
          <div class="flex items-center gap-2 md:gap-4 shrink-0 ml-auto md:ml-0">
            <button v-if="!isAuthed" @click="login"
              class="hidden md:block text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white transition-colors duration-200 font-medium whitespace-nowrap"
            >
              Sign In
            </button>
            
            <!-- User Profile Icon - Desktop -->
            <RouterLink
              v-if="isAuthed"
              to="/profile"
              class="hidden md:flex items-center p-2 rounded-lg transition-colors duration-200 hover:bg-slate-100 dark:hover:bg-gray-800"
              aria-label="User profile"
            >
              <User class="h-5 w-5 text-slate-900 dark:text-white" />
            </RouterLink>

            <RouterLink 
              to="locations" 
              class="hidden md:flex items-center gap-2 bg-indigo-600 dark:bg-indigo-500 text-white px-4 lg:px-5 py-2.5 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0 font-medium whitespace-nowrap"
            >
              <Contact class="h-4 w-4" />
              <span>Contact</span>
            </RouterLink>
            
            <!-- Mobile menu button -->
            <button 
              class="md:hidden relative p-2 rounded-lg transition-colors duration-200 hover:bg-slate-100 dark:hover:bg-gray-800"
              @click="toggleMenu"
              :aria-expanded="isMenuOpen"
              aria-controls="mobile-menu"
              aria-label="Toggle menu"
            >
              <Menu
                v-if="!isMenuOpen"
                class="h-6 w-6 text-slate-900 dark:text-white"
                aria-hidden="true"
              />
              <X
                v-else
                class="h-6 w-6 text-slate-900 dark:text-white"
                aria-hidden="true"
              />
            </button>

            <!-- Dark mode toggle - Desktop only -->
            <button
              @click="toggleDarkMode"
              class="hidden md:flex p-2 rounded-lg transition-colors duration-200 hover:bg-slate-100 dark:hover:bg-gray-800"
              aria-label="Toggle dark mode"
            >
              <Sun v-if="isDarkMode" class="h-5 w-5 text-slate-900 dark:text-white" />
              <Moon v-else class="h-5 w-5 text-slate-900 dark:text-white" />
            </button>
          </div>
        </div>

        <!-- Mobile menu -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-2"
        >
          <div 
            id="mobile-menu"
            v-show="isMenuOpen" 
            class="md:hidden mt-4 pb-4 border-t dark:border-gray-800 bg-white dark:bg-black rounded-b-2xl overflow-y-auto max-h-[calc(100vh-5rem)]"
          >
            <NavLinks 
              :isMobile="true" 
              class="mt-4 bg-white dark:bg-black"
            />

            <!-- Contact Button -->
            <div class="mt-6 mx-4 pt-4 border-t border-slate-200 dark:border-gray-800">
              <RouterLink 
                @click="toggleMenu"
                to="locations" 
                class="flex items-center justify-center space-x-2 bg-indigo-600 dark:bg-indigo-500 text-white px-5 py-3 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg active:translate-y-0.5 font-medium w-full"
              >
                <Contact class="h-4 w-4" />
                <span>Contact Us</span>
              </RouterLink>
            </div>

            <!-- Profile Link - Mobile -->
            <div v-if="isAuthed" class="mt-6 mx-4 pt-4 border-t border-slate-200 dark:border-gray-800">
              <RouterLink 
                @click="toggleMenu"
                to="/profile" 
                class="flex items-center justify-between px-4 py-3 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
              >
                <span>Profile</span>
                <User class="h-5 w-5" />
              </RouterLink>
            </div>

            <div v-if="!isAuthed" class="mt-6 mx-4 pt-4 border-t border-slate-200 dark:border-gray-800">
              <button 
                @click="login"
                class="flex items-center justify-center space-x-2 bg-indigo-600 dark:bg-indigo-500 text-white px-5 py-3 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all duration-200 shadow-md hover:shadow-lg active:translate-y-0.5 font-medium w-full"
              >
                <span>Sign In</span>
              </button>
            </div>



            <!-- Dark mode toggle - Mobile only -->
            <div class="mt-6 mx-4 pt-4 border-t border-slate-200 dark:border-gray-800">
              <button
                @click="toggleDarkMode"
                class="flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
              >
                <span>Dark Mode</span>
                <Sun v-if="isDarkMode" class="h-5 w-5" />
                <Moon v-else class="h-5 w-5" />
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </nav>

    <!-- Overlay (positioned under the nav) -->
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isMenuOpen"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        aria-hidden="true"
        @click="toggleMenu"
      />
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, watch, ref } from 'vue';
import { Menu, X, Contact, Sun, Moon, User } from 'lucide-vue-next';
import { useNavigation } from '@/shared/composables/useNavigation';
import { useAuth } from '@/shared/composables/useAuth';
import { useRoute } from 'vue-router';
import { SITE_NAME } from '@/shared/config/navigation';

import NavLinks from './NavLinks.vue';

const route = useRoute();
const { isScrolled, isMenuOpen, toggleMenu, loadHome } = useNavigation();
const { isAuthed, login } = useAuth();

// Dark mode state
const isDarkMode = ref(localStorage.getItem('theme') === 'dark')

function toggleDarkMode() {
  isDarkMode.value = !isDarkMode.value
  localStorage.setItem('theme', isDarkMode.value ? 'dark' : 'light')
  
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

// Initialize dark mode
onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    isDarkMode.value = savedTheme === 'dark'
  } else {
    isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
  }
})

// Close menu when route changes
watch(route, () => {
  if (isMenuOpen.value) {
    toggleMenu();
  }
});

// Close menu on escape key
onMounted(() => {
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isMenuOpen.value) {
      toggleMenu();
    }
  };
  
  window.addEventListener('keydown', handleEscape);
  onUnmounted(() => {
    window.removeEventListener('keydown', handleEscape);
  });
});
</script>